/**
 * Maison — liste des équipements et fiche détaillée.
 *
 * La liste affiche l'état de chaque appareil d'un coup d'œil : à jour, bientôt,
 * en retard. La fiche montre l'historique d'entretien, les règles actives et le
 * prestataire, avec son numéro appelable en un tap — chercher le numéro du
 * ramoneur est souvent ce qui fait reporter la tâche.
 */

import { useState } from 'react';
import { ArrowLeft, Phone, Plus, RefreshCw } from 'lucide-react';

import { type Equipement, type Tache, aujourdhuiParis, enClair, joursEntre, relatif } from '@foyer/noyau';

import { CarteTache, Chargement, Erreur, TRAIT, Vide } from '../composants/ui.js';
import { useAppli, useRessource, navigue } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';
import { FeuilleTache } from './FeuilleTache.js';

type Etat = 'ajour' | 'bientot' | 'retard' | 'sans';

function etatDe(taches: Tache[], aujourdhui: string): Etat {
  const actives = taches.filter((t) => t.statut === 'active' && t.echeance);
  if (actives.length === 0) return 'sans';
  const pire = Math.max(...actives.map((t) => joursEntre(t.echeance!, aujourdhui)));
  if (pire > 0) return 'retard';
  if (pire > -30) return 'bientot';
  return 'ajour';
}

const LIBELLE_ETAT: Record<Etat, { texte: string; classe: string }> = {
  ajour: { texte: 'à jour', classe: 'pratique' },
  bientot: { texte: 'bientôt', classe: 'constructeur' },
  retard: { texte: 'en retard', classe: 'legale' },
  sans: { texte: 'aucune échéance', classe: 'manuel' },
};

export function Maison() {
  const aujourdhui = aujourdhuiParis();
  const equipements = useRessource(() => donnees.equipements());
  const taches = useRessource(() => donnees.taches({ statut: 'active' }));

  if (equipements.chargement && !equipements.donnee) return <Chargement quoi="Chargement de la maison" />;
  if (equipements.erreur && !equipements.donnee) return <Erreur message={equipements.erreur} />;

  const liste = equipements.donnee?.equipements ?? [];
  const toutes = taches.donnee?.taches ?? [];

  return (
    <>
      <div className="entete-vue">
        <h1>
          Maison
        </h1>
        {liste.length > 0 && (
          <button type="button" className="bouton" onClick={() => navigue('/maison/nouveau')}>
            <Plus strokeWidth={TRAIT} size={18} aria-hidden />
            Déclarer
          </button>
        )}
      </div>

      {liste.length === 0 ? (
        <Vide
          titre="Rien de déclaré."
          texte="Déclarez un premier équipement : l’application en déduira les entretiens, y compris les obligations légales."
        >
          <button type="button" className="bouton engagement" onClick={() => navigue('/maison/nouveau')}>
            Déclarer un équipement
          </button>
        </Vide>
      ) : (
        <div className="grille-cartes deux">
          {liste.map((e) => {
            const siennes = toutes.filter((t) => t.equipementId === e.id);
            const prochaine = siennes
              .filter((t) => t.echeance)
              .sort((a, b) => a.echeance!.localeCompare(b.echeance!))[0];
            const etat = LIBELLE_ETAT[etatDe(siennes, aujourdhui)];
            return (
              <button
                key={e.id}
                type="button"
                className="carte"
                style={{ textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer', margin: 0 }}
                onClick={() => navigue(`/maison/${e.id}`)}
              >
                <div className="entre-deux">
                  <strong>{e.nom}</strong>
                  <span className={`badge ${etat.classe}`}>{etat.texte}</span>
                </div>
                <div className="sourdine">
                  {e.piece ? `${e.piece} · ` : ''}
                  {e.dateInstallation
                    ? `installé le ${enClair(e.dateInstallation)}`
                    : e.dateAchat
                      ? `acheté le ${enClair(e.dateAchat)}`
                      : 'date inconnue'}
                </div>
                <div className="sourdine">
                  {e.dernierEntretien
                    ? `dernier entretien le ${enClair(e.dernierEntretien)}`
                    : 'dernier entretien inconnu'}
                </div>
                {prochaine && (
                  <div style={{ marginTop: 8, fontSize: 13.5 }}>
                    Prochaine : {prochaine.titre} — {relatif(prochaine.echeance!, aujourdhui).texte.toLowerCase()}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

export function DetailEquipement({ id }: { id: string }) {
  const { rafraichis, utilisateur } = useAppli();
  const aujourdhui = aujourdhuiParis();
  const fiche = useRessource(() => donnees.equipement(id), [id]);
  const prestataires = useRessource(() => donnees.prestataires());
  const [message, setMessage] = useState<string | null>(null);
  const [feuille, setFeuille] = useState<Tache | null>(null);
  const [occupe, setOccupe] = useState(false);

  if (fiche.chargement && !fiche.donnee) return <Chargement quoi="Chargement de la fiche" />;
  if (fiche.erreur && !fiche.donnee) return <Erreur message={fiche.erreur} />;
  const equipement: Equipement | undefined = fiche.donnee?.equipement;
  if (!equipement) return <Erreur message="Équipement introuvable" />;

  const taches = fiche.donnee?.taches ?? [];
  const actives = taches.filter((t) => t.statut === 'active');
  const prestataire = prestataires.donnee?.prestataires.find((p) => p.id === equipement.prestataireId);

  async function regenere() {
    setOccupe(true);
    setMessage(null);
    try {
      const r = await donnees.regenereEquipement(id);
      const gelees = r.ignorees.filter((i) => i.motif === 'intervention_utilisateur').length;
      const tombes = r.ignorees.filter((i) => i.motif === 'tombe').length;
      setMessage(
        [
          `${r.creees.length} échéance(s) créée(s)`,
          `${r.misesAJour.length} mise(s) à jour`,
          gelees > 0 ? `${gelees} laissée(s) telle(s) quelle(s), vous les aviez ajustées` : null,
          tombes > 0 ? `${tombes} non recréée(s), vous les aviez supprimées` : null,
        ]
          .filter(Boolean)
          .join(' · '),
      );
      rafraichis();
      fiche.recharge();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setOccupe(false);
    }
  }

  async function coche(tache: Tache) {
    setOccupe(true);
    try {
      await donnees.coche(tache.id, tache.titre, { majDernierEntretien: true });
      rafraichis();
      fiche.recharge();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setOccupe(false);
    }
  }

  return (
    <>
      <div className="entete-vue">
        <h1>{equipement.nom}</h1>
        <button type="button" className="bouton texte" onClick={() => navigue('/maison')}>
          <ArrowLeft strokeWidth={TRAIT} size={16} aria-hidden />
          Maison
        </button>
      </div>

      <div className="carte">
        <div className="sourdine">
          {equipement.piece ? `${equipement.piece} · ` : ''}
          {equipement.dateInstallation ? `installé le ${enClair(equipement.dateInstallation)}` : ''}
          {equipement.dateAchat ? ` · acheté le ${enClair(equipement.dateAchat)}` : ''}
        </div>
        <div style={{ marginTop: 6 }}>
          Dernier entretien :{' '}
          <strong>
            {equipement.dernierEntretien ? enClair(equipement.dernierEntretien) : 'inconnu'}
          </strong>
        </div>
        {Object.keys(equipement.caracteristiques).length > 0 && (
          <div className="sourdine" style={{ marginTop: 4 }}>
            {Object.entries(equipement.caracteristiques)
              .map(([cle, v]) => `${cle.replace(/_/g, ' ')} : ${String(v)}`)
              .join(' · ')}
          </div>
        )}
        {prestataire && (
          <div className="actions">
            {prestataire.telephone ? (
              <a className="bouton secondaire" href={`tel:${prestataire.telephone.replace(/\s/g, '')}`}>
                <Phone strokeWidth={TRAIT} size={16} aria-hidden />
                {prestataire.nom}
              </a>
            ) : (
              <span className="badge">{prestataire.nom}</span>
            )}
          </div>
        )}
      </div>

      {message && <div className="note">{message}</div>}

      <div className="section-titre" style={{ cursor: 'default' }}>
        Échéances actives <span className="compte">{actives.length}</span>
      </div>
      {actives.length === 0 && <div className="note">Aucune échéance active pour cet équipement.</div>}
      {actives.map((t) => (
        <CarteTache
          key={t.id}
          tache={t}
          aujourdhui={aujourdhui}
          assigneAutre={t.assigneA && t.assigneA !== utilisateur?.id ? 'autre' : null}
          occupe={occupe}
          onCoche={() => void coche(t)}
          onOuvre={() => setFeuille(t)}
        />
      ))}

      <div className="actions">
        <button type="button" className="bouton secondaire" onClick={() => void regenere()} disabled={occupe}>
          <RefreshCw strokeWidth={TRAIT} size={16} aria-hidden />
          Recalculer depuis le catalogue
        </button>
      </div>
      <p className="sourdine">
        Le recalcul ne touche ni les échéances que vous avez ajustées, ni celles que vous avez
        supprimées.
      </p>

      {feuille && <FeuilleTache tache={feuille} onFerme={() => setFeuille(null)} onEnregistre={() => fiche.recharge()} />}
    </>
  );
}
